import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import CardNumberModel from './CardNumberModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface CardNumbers {
  card_id: number;
  card_number: string;
  card_status: number;
  }

const CardNumbers: React.FC = () => {
  const [cardnumbers, setCardNumbers] = useState<CardNumbers[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<CardNumbers | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchCardNumbers = async () => {
    try {
      const data = await apiRequest<CardNumbers[]>({
        method: 'get',
        url: 'card',
      });
      setCardNumbers(data);
    } catch (error) {
      showError('Failed to fetch card numbers');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardNumbers();
  }, []);

  const handleEditClick = (record: CardNumbers) => {
    setIsEditMode(true);
    setInitialValues({
      card_id: record.card_id,
      card_number: record.card_number,
      card_status: record.card_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchCardNumbers();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (card_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-card/${card_id}`,
      });
      showSuccess('card number deleted');
      fetchCardNumbers();
    } catch {
      showError('Failed to delete card number');
    }
  };

  const handleStatusToggle = async (CardNumberId: number) => {
    setCardNumbers((prev) =>
      prev.map((item) =>
        item.card_id === CardNumberId
          ? { ...item, card_status: item.card_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-card/${CardNumberId}`,
      });
      showSuccess('card number status updated');
    } catch (error) {
      showError('Failed to update status');
      setCardNumbers((prev) =>
        prev.map((item) =>
          item.card_id === CardNumberId
            ? { ...item, card_status: item.card_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<CardNumbers>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<CardNumbers> = [
    {
      title: 'Card Number',
      dataIndex: 'card_number',
      key: 'card_number',
      filters: Array.from(new Set(cardnumbers.map((s) => s.card_number))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.card_number || null,
      onFilter: (value, record) => record.card_number.includes(value as string),
      sorter: (a, b) => a.card_number.localeCompare(b.card_number),
      sortOrder: sortedInfo.columnKey === 'card_number' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.card_number}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: CardNumbers) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.card_status}
                onChange={() => handleStatusToggle(record.card_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this card number?"
              onConfirm={() => handleDelete(record.card_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Card Numbers</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Card Number
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<CardNumbers>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="card_id"
          dataSource={cardnumbers.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No card number found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, cardnumbers.length)} of{' '}
                {cardnumbers.length}
              </span>
              <Pagination
                {...pagination}
                total={cardnumbers.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <CardNumberModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default CardNumbers;